// Transitions PR-5 — the guided execute-assumption flow. Replaces PR-4a's
// single-screen confirm (ExecuteAssumptionModal): the same D-26 execution,
// now stepped — pick the incoming obligor, read every perimeter consequence
// with the D-23/D-24 fee amounts from the loan's real balance, see the
// guaranty truth-from-data (§10.8 — nothing is released), then confirm.

import { useEffect, useState } from "react";
import { MessageCircleQuestion } from "lucide-react";
import { Button, Label, StatusStepper, Textarea } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { extractErrorMessage } from "@/api/client";
import { formatCurrency } from "@/design-system/lib/format";
import {
  CounterpartyPicker,
  type SelectedCounterparty,
} from "../parties/CounterpartyPicker";
import { useExecuteAssumption } from "@/hooks/portfolioAdmin/useLoanTransitions";
import type { LoanTransitionDto } from "@/api/portfolioAdmin/loanTransitionsApi";
import type { LoanSummaryDto } from "@/api/portfolioAdmin/types";
import { GuarantyFactsPanel } from "./GuarantyFactsPanel";
import { TransitionExplainModal } from "./TransitionExplainModal";
import {
  ASSUMPTION_FEE_FACT,
  HARDSHIP_FRAMING,
  PATH_FACTS,
  PERIMETER_CONSEQUENCES,
} from "./transitionsWizardCopy";

const STEPS = [
  { key: "obligor", label: "Incoming buyer" },
  { key: "changes", label: "What changes hands" },
  { key: "guaranties", label: "Guaranties" },
  { key: "confirm", label: "Confirm & execute" },
] as const;

export function ExecuteAssumptionWizard({
  loan,
  transition,
  open,
  onClose,
}: {
  loan: LoanSummaryDto;
  transition: LoanTransitionDto;
  open: boolean;
  onClose: () => void;
}) {
  const mutation = useExecuteAssumption(loan.id);
  const isHardship = transition.type === "HardshipExit";
  const [step, setStep] = useState(0);
  const [obligorKind, setObligorKind] = useState<"Person" | "LegalEntity">("Person");
  const [obligor, setObligor] = useState<SelectedCounterparty | null>(null);
  const [note, setNote] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [askAiOpen, setAskAiOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(0);
      setObligorKind("Person");
      setObligor(null);
      setNote("");
      setAcknowledged(false);
      setError(null);
      setAskAiOpen(false);
    }
  }, [open]);

  const stepKey = STEPS[step].key;
  const isLast = step === STEPS.length - 1;
  const stepValid = stepKey === "obligor" ? obligor != null : stepKey !== "confirm" || acknowledged;

  const submit = async () => {
    if (!obligor) return;
    setError(null);
    try {
      await mutation.mutateAsync({
        transitionId: transition.id,
        body: {
          newBorrowerPersonId: obligorKind === "Person" ? obligor.id : null,
          newBorrowerEntityId: obligorKind === "LegalEntity" ? obligor.id : null,
          note: note.trim() || null,
        },
      });
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const fee = (pct: number) => formatCurrency(loan.currentBalance * pct);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isHardship ? "Execute hardship assumption" : "Execute assumption"}
      description={
        isHardship
          ? "The expedited hardship transfer (§10.7, 30-day track): the loan changes hands to the incoming buyer now. This cannot be undone here."
          : "The loan changes hands to the incoming buyer now. This cannot be undone here."
      }
      size="lg"
      dismissOnBackdropClick={false}
      footer={
        <div className="flex items-center justify-end gap-space-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={mutation.isPending}
            data-testid="assumption-wizard-cancel"
          >
            Cancel
          </Button>
          {step > 0 && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => setStep((s) => Math.max(s - 1, 0))}
              disabled={mutation.isPending}
              data-testid="assumption-wizard-back"
            >
              Back
            </Button>
          )}
          <Button
            type="button"
            onClick={() => (isLast ? void submit() : setStep((s) => s + 1))}
            disabled={!stepValid || mutation.isPending}
            data-testid={isLast ? "execute-assumption-submit" : "assumption-wizard-next"}
          >
            {mutation.isPending ? "Executing…" : isLast ? "Execute assumption" : "Next"}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-space-4" data-testid="execute-assumption-wizard">
        <div className="flex items-start justify-between gap-space-2">
          <StatusStepper
            steps={STEPS.map((s) => ({ key: s.key, label: s.label }))}
            currentIndex={step}
            label="Execute assumption steps"
            data-testid="assumption-wizard-stepper"
          />
          <Button
            type="button"
            variant="text"
            size="sm"
            onClick={() => setAskAiOpen(true)}
            data-testid="ask-ai-button"
          >
            <MessageCircleQuestion size={14} aria-hidden /> Ask AI
          </Button>
        </div>

        {stepKey === "obligor" && (
          <div className="flex flex-col gap-space-3">
            <div className="flex gap-space-3" role="radiogroup" aria-label="New buyer record type">
              {(["Person", "LegalEntity"] as const).map((kind) => (
                <label key={kind} className="flex items-center gap-space-1 text-small">
                  <input
                    type="radio"
                    name="obligor-kind"
                    checked={obligorKind === kind}
                    onChange={() => {
                      setObligorKind(kind);
                      setObligor(null);
                    }}
                    data-testid={`obligor-kind-${kind}`}
                  />
                  {kind === "Person" ? "Person" : "Legal entity"}
                </label>
              ))}
            </div>
            <CounterpartyPicker
              counterpartyType={obligorKind}
              value={obligor}
              onChange={setObligor}
            />
          </div>
        )}

        {stepKey === "changes" && (
          <div className="flex flex-col gap-space-3">
            {isHardship && (
              <div className="rounded border border-border-light bg-white-warm p-space-3">
                <p className="text-small m-0" data-testid="hardship-framing">
                  {HARDSHIP_FRAMING}
                </p>
              </div>
            )}
            <div className="rounded border border-border-light bg-white-warm p-space-3">
              <p className="text-small font-semibold m-0 mb-space-1">
                What happens when you execute:
              </p>
              <ul
                className="m-0 pl-space-4 flex flex-col gap-space-1"
                data-testid="assumption-consequences"
              >
                {PERIMETER_CONSEQUENCES.map((c) => (
                  <li key={c} className="text-small">
                    {c}
                  </li>
                ))}
              </ul>
            </div>
            <p className="text-small m-0" data-testid="assumption-fee-line">
              {ASSUMPTION_FEE_FACT} On today's balance that is about{" "}
              <span className="font-semibold">{fee(0.03)}</span> ({fee(0.01)} processing +{" "}
              {fee(0.02)} assumption), collected as part of executing.
            </p>
          </div>
        )}

        {stepKey === "guaranties" && <GuarantyFactsPanel loanId={loan.id} />}

        {stepKey === "confirm" && (
          <div className="flex flex-col gap-space-3">
            <p className="text-small m-0" data-testid="confirm-summary">
              Executing hands the loan to{" "}
              <span className="font-semibold">{obligor?.name ?? "the selected buyer"}</span>, runs
              the full identity/consent perimeter, and records the assumption fees.
            </p>
            <div>
              <Label htmlFor="assumption-note">Note (optional)</Label>
              <Textarea
                id="assumption-note"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
            <label className="flex items-start gap-space-2 text-small">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                data-testid="assumption-acknowledge"
              />
              <span>
                I understand the current borrower loses portal and payment access and the new buyer
                must re-consent to payment.
              </span>
            </label>
          </div>
        )}

        {error && (
          <p
            className="text-small text-status-error m-0"
            role="alert"
            data-testid="execute-assumption-error"
          >
            {error}
          </p>
        )}

        {askAiOpen && (
          <TransitionExplainModal
            loanId={loan.id}
            context={{
              pathType: transition.type,
              pathFacts: PATH_FACTS[transition.type],
              stepKey: STEPS[step].key,
              transitionId: transition.id,
            }}
            onClose={() => setAskAiOpen(false)}
          />
        )}
      </div>
    </Modal>
  );
}
